import { useCallback, useEffect, useMemo, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronLeft, faChevronRight } from "@awesome.me/kit-70c1f0055f/icons/duotone/solid";
import { Button } from "@/components/ui/button/Button";
import { CC_ERROR_TEXT, CC_GHOST_BTN, CC_PANEL } from "@/components/case-checking/caseCheckingChrome";
import { ProblemStatementsTab } from "@/components/case-checking/ProblemStatementsTab";
import { ReviewOverallSignoffCard } from "@/components/case-checking/ReviewOverallSignoffCard";
import { useProblemStatementRun } from "@/components/case-checking/useProblemStatementRun";
import type { CaseCheckingGrade } from "@/components/case-checking/caseCheckingStatusBadges";
import {
  patchProblemStatementHumanReviews,
  type CaseReview as ApiCaseReview,
  type ProblemStatementHumanReviewPatch,
} from "@/services/api/case-checking";
import { finalOutcomeId } from "@/components/case-checking/outcomeCatalog";
import clsx from "clsx";

/**
 * Carousel deepdive — one statement at a time with prev/next.
 * Reuses peer sign-off card rendering from ProblemStatementsTab via embedded mode.
 */
export function ReviewDeepdiveTab({
  caseId,
  focusStatementId,
  focusRuleId = null,
  enterKey = 0,
  caseReview,
  fallbackGrade,
  readOnly = false,
  companyId,
}: {
  caseId: string;
  focusStatementId: string | null;
  focusRuleId?: string | null;
  enterKey?: number;
  caseReview?: ApiCaseReview | null;
  fallbackGrade: CaseCheckingGrade;
  readOnly?: boolean;
  companyId?: string;
}) {
  const overallStatementId = finalOutcomeId(companyId);
  const { run, setRun, loading, scorable, humanReviewsById } = useProblemStatementRun(caseId);
  const [index, setIndex] = useState(0);
  const [highlight, setHighlight] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [overallSaveState, setOverallSaveState] = useState<"idle" | "saving" | "saved">("idle");

  const totalSteps = readOnly ? scorable.length : scorable.length + 1;
  const isOverallStep = !readOnly && index >= scorable.length;

  const focusIndex = useMemo(() => {
    if (!focusStatementId || scorable.length === 0) return 0;
    const i = scorable.findIndex((r) => r.id === focusStatementId);
    return i >= 0 ? i : 0;
  }, [focusStatementId, scorable]);

  useEffect(() => {
    setIndex(focusIndex);
  }, [focusIndex, focusStatementId]);

  useEffect(() => {
    if (!focusStatementId || enterKey === 0) return;
    setHighlight(true);
    const timer = window.setTimeout(() => setHighlight(false), 900);
    return () => window.clearTimeout(timer);
  }, [focusStatementId, enterKey]);

  const saveOverallReview = useCallback(
    async (review: ProblemStatementHumanReviewPatch) => {
      if (!run) return;
      setOverallSaveState("saving");
      try {
        const updated = await patchProblemStatementHumanReviews(caseId, run.id, [review]);
        setRun(updated);
        setOverallSaveState("saved");
        setTimeout(() => setOverallSaveState("idle"), 2000);
      } catch (e) {
        setSaveError(e instanceof Error ? e.message : "Failed to save overall review");
        setOverallSaveState("idle");
      }
    },
    [caseId, run, setRun],
  );

  if (loading) {
    return <p className="text-sm text-slate-400">Loading deepdive…</p>;
  }

  if (!run || scorable.length === 0) {
    return (
      <div className={CC_PANEL} data-testid="review-deepdive-empty">
        <p className="text-sm text-slate-600 dark:text-slate-300">No statements to review yet.</p>
      </div>
    );
  }

  const current = isOverallStep ? null : scorable[index];
  const atStart = index <= 0;
  const atEnd = index >= totalSteps - 1;

  return (
    <div className="space-y-3" data-testid="review-deepdive-tab">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Button
            plain
            type="button"
            disabled={atStart}
            onClick={() => setIndex((i) => Math.max(0, i - 1))}
            className={clsx(CC_GHOST_BTN, "py-2 disabled:opacity-40")}
            data-testid="review-deepdive-prev"
          >
            <FontAwesomeIcon icon={faChevronLeft} className="h-3 w-3" />
            Previous
          </Button>
        </div>
        <div className="min-w-0 text-center">
          <p className="text-xs text-slate-500">
            {isOverallStep ? "Final step" : "Statement"} {index + 1} of {totalSteps}
            {isOverallStep ? " · " + overallStatementId : current ? ` · ${current.id}` : ""}
          </p>
          <p className="mt-0.5 truncate text-sm font-medium text-slate-700 dark:text-slate-200">
            {isOverallStep ? "Final Suitability Assessment" : current?.title}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            plain
            type="button"
            disabled={atEnd}
            onClick={() => setIndex((i) => Math.min(totalSteps - 1, i + 1))}
            className={clsx(CC_GHOST_BTN, "py-2 disabled:opacity-40")}
            data-testid="review-deepdive-next"
          >
            Next
            <FontAwesomeIcon icon={faChevronRight} className="h-3 w-3" />
          </Button>
        </div>
      </div>

      {saveError ? <p className={clsx("text-xs", CC_ERROR_TEXT)}>{saveError}</p> : null}

      <div
        className={clsx(
          "transition-all duration-500 ease-out",
          highlight ? "ring-2 ring-violet-400/40 ring-offset-2 ring-offset-slate-950 rounded-xl" : "ring-0",
        )}
        data-testid="review-deepdive-card-shell"
      >
        {isOverallStep ? (
          <ReviewOverallSignoffCard
            caseReview={caseReview ?? null}
            fallbackGrade={fallbackGrade}
            savedReview={humanReviewsById.get(overallStatementId)}
            onSave={(review) => void saveOverallReview(review)}
            saveState={overallSaveState}
            companyId={companyId}
          />
        ) : (
          <ProblemStatementsTab
            caseId={caseId}
            peerReviewMode
            deepdiveOnly
            focusedStatementId={current?.id ?? null}
            focusedRuleId={focusRuleId}
            readOnly={readOnly}
          />
        )}
      </div>
    </div>
  );
}
